import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, Col, Container, Row, Badge } from 'reactstrap';
import { useProfile } from '../../../Components/Hooks/UserHooks';

/**
 * What it costs.
 *
 * The site had no price on it anywhere. That is a conversion problem and, more
 * pressingly, a live-account problem: a merchant of record reviewing this
 * before switching on real payments expects to find what is sold and for how
 * much, beside the terms, the cancellation policy and a way to reach a human.
 * Everything else on that list already existed.
 *
 * WHAT THE TIERS DIFFER BY. This used to promise they differed by monthly
 * allowance and by nothing else. That was true when a member's own questions
 * were the whole product, and it is not true now: running the standing
 * experiments and reading the shared room both need a subscription, and more
 * will be added to that side over time.
 *
 * So the member list is written to be extended rather than to be exhaustive.
 * The one claim to keep making is the one that stays true whatever gets added:
 * paying does not buy a better answer. The research, the scoring and the record
 * are the same on both plans -- a subscription buys more of the tool, not a
 * thumb on the scale. Never restate "the only difference is volume"; a promise
 * a member discovers to be false the day after paying is the one thing this
 * section must never contain.
 *
 * The numbers mirror backend/config/tiers.json, which is the source of truth
 * the server actually enforces. They are repeated rather than fetched: this is
 * a static marketing section that must render for a signed-out visitor with no
 * API call, and a price that renders late or not at all is worse than one kept
 * in step by hand. Change tiers.json and change these.
 *
 * Neither button subscribes. Subscribing happens in one place — the profile
 * page — which already knows about test mode, the admin gate and the
 * early-access request. A second copy of that state machine in a marketing
 * section would drift from it.
 */

const FREE_PROMPTS = 15;
const MEMBER_PROMPTS = 100;
const MEMBER_PRICE = '9.99';

const Tick = () => (
    <i className="ri-checkbox-circle-fill text-success fs-15 align-middle me-2" />
);

const Pricing = () => {
    const { userProfile, token } = useProfile();
    const signedIn = !!(userProfile && token);

    // Signed out, both roads lead to an account; signed in, to the one page
    // where a plan is actually changed.
    const destination = signedIn ? '/profile' : '/login';

    const shared = [
        'Research prompts built for any asset you name',
        'Answers recorded and scored when the horizon closes',
        'Your own scorecard — hit rate, range accuracy, calibration',
        'Unlimited history and scoring, on both plans',
        "The four standing experiments, public once they settle",
    ];

    // What the subscription adds beyond volume. Expected to grow, so it is a
    // list rather than a claim about the whole difference. Every line is
    // something the server actually gates on -- add here only when it does.
    const memberOnly = [
        'Run any of the four standing experiments with your own AI',
        "Read every other subscriber's runs, credited to whoever made them",
        'Follow the standing experiments before they settle',
    ];

    return (
        <React.Fragment>
            <section className="section" id="pricing">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <div className="text-center mb-5">
                                <h3 className="mb-3 fw-semibold">What it costs</h3>
                                <p className="text-muted mb-0">
                                    One paid plan, billed monthly. It buys more room to work and
                                    the parts of the tool built for people who use it daily. The
                                    research itself, the scoring and the record are identical on
                                    both — paying does not buy a better answer.
                                </p>
                            </div>
                        </Col>
                    </Row>

                    <Row className="gy-4 justify-content-center">
                        <Col lg={5} md={8}>
                            <Card className="plan-box mb-0 h-100">
                                <CardBody className="p-4 m-2 d-flex flex-column">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h5 className="mb-1 fw-semibold">Free</h5>
                                            <p className="text-muted mb-0">Enough to see whether this suits you</p>
                                        </div>
                                        <div className="avatar-sm">
                                            <div className="avatar-title bg-light rounded-circle text-primary">
                                                <i className="ri-book-mark-line fs-20" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="py-4 text-center">
                                        <h1 className="mb-0">
                                            <sup><small>$</small></sup>
                                            <span className="ff-secondary fw-bold">0</span>
                                        </h1>
                                    </div>

                                    <div className="text-center mb-4">
                                        <Badge color="light" className="text-body fs-13 px-3 py-2">
                                            {FREE_PROMPTS} research prompts a month
                                        </Badge>
                                    </div>

                                    <ul className="list-unstyled text-muted vstack gap-3 ff-secondary flex-grow-1">
                                        {shared.map((item) => (
                                            <li key={item}><Tick />{item}</li>
                                        ))}
                                    </ul>

                                    <div className="mt-4">
                                        <Link to={destination} className="btn btn-soft-primary w-100">
                                            {signedIn ? 'Your account' : 'Create a free account'}
                                        </Link>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={5} md={8}>
                            <Card className="plan-box mb-0 h-100 border border-primary">
                                <CardBody className="p-4 m-2 d-flex flex-column">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h5 className="mb-1 fw-semibold">
                                                Member{' '}
                                                <Badge color="primary" className="align-middle ms-1">
                                                    Room to use it
                                                </Badge>
                                            </h5>
                                            <p className="text-muted mb-0">For research as a habit, not a trial</p>
                                        </div>
                                        <div className="avatar-sm">
                                            <div className="avatar-title bg-primary-subtle rounded-circle text-primary">
                                                <i className="ri-medal-line fs-20" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="py-4 text-center">
                                        <h1 className="mb-0">
                                            <sup><small>$</small></sup>
                                            <span className="ff-secondary fw-bold">{MEMBER_PRICE}</span>
                                            <span className="fs-13 text-muted"> /month</span>
                                        </h1>
                                    </div>

                                    <div className="text-center mb-4">
                                        <Badge color="primary" className="fs-13 px-3 py-2">
                                            {MEMBER_PROMPTS} research prompts a month
                                        </Badge>
                                    </div>

                                    <ul className="list-unstyled text-muted vstack gap-3 ff-secondary flex-grow-1">
                                        <li>
                                            <Tick />
                                            <span className="text-body">
                                                Everything in Free, at {Math.round(MEMBER_PROMPTS / FREE_PROMPTS)}×
                                                the monthly allowance
                                            </span>
                                        </li>
                                        {shared.slice(1).map((item) => (
                                            <li key={item}><Tick />{item}</li>
                                        ))}
                                        {memberOnly.map((item) => (
                                            <li key={item}>
                                                <Tick />
                                                <span className="text-body">{item}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="mt-4">
                                        <Link to={destination} className="btn btn-primary w-100">
                                            {signedIn ? 'Go to your plan' : 'Get started'}
                                        </Link>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/*
                        The terms that decide whether somebody feels misled later:
                        what renews, what happens when they stop, and the fact that
                        the number above is not the number on the card. A Canadian
                        member is charged about $11.29 once Stripe adds their sales
                        tax, and finding that out at checkout is a bad moment.
                    */}
                    <Row className="justify-content-center mt-5">
                        <Col lg={8}>
                            <div className="text-center">
                                <p className="text-muted mb-2">
                                    Billed monthly and cancellable at any time from your profile.
                                    Cancelling stops the next payment and you keep access until the
                                    month you have paid for ends. We do not refund payments already
                                    made — see the{' '}
                                    <Link to="/refund-policy">Cancellation and Refund Policy</Link>.
                                </p>
                                <p className="text-muted mb-0 fs-13">
                                    Prices are in US dollars and exclude sales tax, which Stripe
                                    calculates and adds at checkout for where you live. Stripe is the
                                    merchant of record and handles the payment; we never see your card
                                    details. Allowances run per calendar month and do not carry over.
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
};

export default Pricing;
